/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import WebGLRenderGraph from "App/render/webgl/render_graph";
import Appearance from "App/geometry/material/appearance";
import WebGLContext from "App/web_gl_context";
import Canvas2DContext from "App/canvas_2d_context";
import Factory from "App/render/webgl/nodes/factory";
import ModelStatistics from "App/model_statistics";
import RenderObject from "App/geometry/objects/object";
import Pass from "App/render/webgl/passes/pass";
import {
    TransformNodeTypeName,
    BatchesManagerNodeTypeName,
    MaterialNodeTypeName,
    RenderOptionNodeTypeName,
    ShapeBatchNodeTypeName,
    TextureNodeTypeName
} from "App/render/webgl/nodes/type_names";
import IScene from "js_interface/src/scene_interface";

/**
 * @param {SceneAPI.Scene} aSceneAPI
 * @param {WebGLRenderGraph} aRenderGraph
 * @param {Object} aParentNode
 * @param {Appearance} aAppearance
 * @constructor
 */
export default
class RenderNode {

    private static ID: number = 0;

    protected _sceneAPI: IScene;
    protected _renderGraph: WebGLRenderGraph;
    protected _parent: RenderNode;
    protected childnodes: RenderNode[];
    protected renderableReady: boolean;
    protected renderFilter: number;
    protected _context: WebGLContext | Canvas2DContext;
    protected appearance: Appearance;
    protected _structure: string[];
    protected _renderReturnVal: boolean;
    protected _typeName: string;

    private renderNodeID: number;
    private _depth: number;
    private _isFromInternalModel: boolean;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aParentNode: RenderNode, aShape: RenderObject) {
        this._sceneAPI = aSceneAPI;
        this._renderGraph = aRenderGraph;

        this._parent = aParentNode;
        this.childnodes = [];

        this.renderableReady = false;

        this.renderNodeID = RenderNode.ID++;

        // this contains a number which expresses what shapes/options will be rendered during
        // the render graph traversal - keepCameraAbove, pickable, alwaysOnTop, etc.
        this.renderFilter = 0;

        this._context = this._renderGraph.getContext();

        this.appearance = aShape !== null ? aShape.appearance : null;

        this._depth = 0;

        this._structure = [
            TransformNodeTypeName,
            TextureNodeTypeName,
            MaterialNodeTypeName,
            RenderOptionNodeTypeName,
            BatchesManagerNodeTypeName,
            ShapeBatchNodeTypeName
        ];

        this._renderReturnVal = false;
    }

    public getUID(): number {
        return this.renderNodeID;
    }

    public getTypeName(): string {
        return this._typeName;
    }

    public getParent(): RenderNode {
        return this._parent;
    }

    public getAppearance() {
        return this.appearance;
    }

    public hasChildren(): boolean {
        return this.childnodes.length > 0;
    }

    public compareProperties(aShape: RenderObject): boolean {
        return false;
    }

    public addShape(aShape: RenderObject) {
        // create empty texture or texture based on its texture.properties
        let node = this.findChildNode(this._structure[this._depth], aShape);
        if (node === null) {
            node = Factory.create(this._structure[this._depth], this._sceneAPI, this._renderGraph, this, aShape);
            node._structure = this._structure;
            node._depth = this._depth + 1;
            node._isFromInternalModel = aShape.getModel().isInternal();
            this.childnodes.push(node);
        }

        return node.addShape(aShape);
    }

    public findChildNode(aTypeName: string, aShape: RenderObject) {
        let len = this.childnodes.length;
        for (let i = 0; i < len; i++) {
            let childNode = this.childnodes[i];
            if (childNode._typeName === aTypeName) {
                if (childNode.compareProperties(aShape)) {
                    return childNode;
                }
            }
        }

        return null;
    }

    public getIsFromInternalModel(): boolean {
        return this._isFromInternalModel;
    }

    public isEmpty(): boolean {
        return (this.childnodes.length === 0);
    }

    public removeShape(aShape: RenderObject): boolean {
        for (let child of this.childnodes) {
            if (child.removeShape(aShape)) {
                return true;
            }
        }

        return false;
    }

    public childRemoved(aRemovedChild: RenderNode) {
        for (let i = 0; i < this.childnodes.length; i++) {
            if (this.childnodes[i].renderNodeID === aRemovedChild.renderNodeID) {
                this.childnodes[i].removed();
                this.childnodes.splice(i, 1);
                break;
            }
        }

        if (this.isEmpty()) {
            this._parent.childRemoved(this);
        }
    }

    /**
     * Called right before this node is removed
     */
    public removed() {
        //
    }

    public reserveBuffers() {
        for (let i = 0; i !== this.childnodes.length; i++) {
            this.childnodes[i].reserveBuffers();
        }
    }

    public reCreateBuffers() {
        for (let i = 0; i !== this.childnodes.length; i++) {
            this.childnodes[i].reCreateBuffers();
        }
    }

    public updateBuffers() {
        for (let i = 0; i !== this.childnodes.length; i++) {
            this.childnodes[i].updateBuffers();
        }
    }

    public getShapes(aShapes: RenderObject[]) {
        for (let i = 0; i !== this.childnodes.length; i++) {
            this.childnodes[i].getShapes(aShapes);
        }
    }

    public countShapesAndUniqueTextures(aStatistics: ModelStatistics) {
        for (let i = 0; i !== this.childnodes.length; i++) {
            this.childnodes[i].countShapesAndUniqueTextures(aStatistics);
        }
    }

    public render(aRenderPass: Pass) {
        this._renderReturnVal = false;
        for (let i = 0; i !== this.childnodes.length; i++) {
            this._renderReturnVal =  this.childnodes[i].render(aRenderPass) || this._renderReturnVal;
        }

        return this._renderReturnVal;
    }

    public release() {
        for (let i = 0; i !== this.childnodes.length; i++) {
            this.childnodes[i].release();
        }
    }
}
